import React from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getFilteredRowModel,
  createColumnHelper,
  flexRender,
  getSortedRowModel,
} from '@tanstack/react-table';
import { Button } from '../../components/ui/button';
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from '../../components/ui/table';
import { formatDate, formatFileSize, getFileType, getFilename } from './format';
import { useAuth0 } from '@auth0/auth0-react';
import { getAccessToken } from '../../utils/authToken';
import { downloadFile } from '../../utils/downloadFile';
import { TablePagination } from '../table-common/tablePagination';
import { Download, Loader2 } from 'lucide-react';
import { TextFilter } from '../table-common/filters/text-filter';
import { DateRange } from 'react-day-picker';
import { DateFilter } from '../table-common/filters/date-filter';

type FileInfo = {
  name: string;
  updated: string;
  size: number;
  content_type: string;
};

interface FileListProps {
  files: FileInfo[];
  isLoading: boolean;
  error: string;
  onRefresh: () => void;
}

const columnHelper = createColumnHelper<FileInfo>();

const FileList: React.FC<FileListProps> = ({
  files,
  isLoading,
  error,
  onRefresh,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [downloadingFile, setDownloadingFile] = React.useState<string | null>(
    null
  );
  const columns = [
    columnHelper.display({
      id: 'serial',
      header: 'Sl No.',
      cell: (info) => info.row.index + 1,
    }),
    columnHelper.accessor('name', {
      header: 'File Name',
      cell: (info) => (
        <div className="font-medium">{getFilename(info.getValue())}</div>
      ),
    }),
    columnHelper.accessor('updated', {
      id: 'updated',
      header: 'Uploaded Date',
      cell: (info) => formatDate(info.getValue()),
      filterFn: (row, columnId, filterValue: DateRange | undefined) => {
        if (!filterValue || !filterValue.from || !filterValue.to) return true;
        const rowDate = new Date(row.getValue(columnId));
        const from = new Date(filterValue.from);
        const to = new Date(filterValue.to);
        to.setHours(23, 59, 59, 999);
        return rowDate >= from && rowDate <= to;
      },
      enableSorting: true,
    }),
    columnHelper.accessor('size', {
      header: 'Size',
      cell: (info) => formatFileSize(info.getValue()),
    }),
    columnHelper.display({
      id: 'type',
      header: 'Type',
      cell: (info) => (
        <span className="inline-flex rounded-md border border-border bg-muted px-2 py-0.5 text-xs font-medium text-foreground">
          {getFileType(info.row.original.name)}
        </span>
      ),
    }),
    columnHelper.display({
      id: 'download',
      header: 'Download',
      cell: (info) => {
        const fileName = info.row.original.name;
        const handleDownload = async () => {
          setDownloadingFile(fileName);
          try {
            const authToken = await getAccessToken(getAccessTokenSilently);
            await downloadFile(authToken, fileName);
          } finally {
            setDownloadingFile(null);
          }
        };
        return (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleDownload}
            className="inline-flex items-center gap-1 text-xs"
            disabled={downloadingFile === fileName}
          >
            {downloadingFile === fileName ? (
              <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
            ) : (
              <Download className="h-4 w-4 shrink-0" aria-hidden />
            )}
            Download
          </Button>
        );
      },
    }),
  ];

  const table = useReactTable<FileInfo>({
    data: files,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    initialState: {
      sorting: [
        {
          id: 'updated',
          desc: true,
        },
      ],
      pagination: {
        pageIndex: 0,
        pageSize: 10,
      },
    },
  });

  if (isLoading) {
    return (
      <div className="flex flex-col justify-center items-center h-64 space-y-4">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-primary" />
        <span className="text-muted-foreground">Loading files...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col justify-center items-center h-64 space-y-4">
        <div className="text-center text-destructive">
          <h3 className="mb-2 text-lg font-semibold">Error Loading Files</h3>
          <p className="text-sm">{error}</p>
        </div>
        <Button type="button" variant="outline" onClick={onRefresh}>
          Retry
        </Button>
      </div>
    );
  }

  if (!files || files.length === 0) {
    return (
      <div className="flex flex-col justify-center items-center h-64 space-y-4">
        <div className="text-center text-muted-foreground">
          <h3 className="mb-2 text-lg font-semibold text-foreground">No Files Found</h3>
          <p className="text-sm">The storage bucket appears to be empty.</p>
        </div>
        <Button type="button" variant="outline" onClick={onRefresh}>
          Refresh
        </Button>
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col gap-2 p-8 pt-0 pb-0">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-xl font-semibold text-foreground">
          Files ({files.length})
        </h2>
        <Button
          type="button"
          variant="outline"
          onClick={onRefresh}
          disabled={isLoading}
        >
          Refresh
        </Button>
      </div>
      <div className="max-h-[calc(100vh-220px)] overflow-auto overflow-x-auto rounded-[10px] border border-border bg-background shadow-lg">
        <Table>
          <TableHeader className="sticky top-0 bg-muted z-10">
            {table.getHeaderGroups().map((headerGroup) => (
              <React.Fragment key={headerGroup.id}>
                <TableRow className="!border-b-0 bg-muted">
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  ))}
                </TableRow>
                <TableRow className="border-t-0 bg-muted">
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id} className="border-t-0">
                      {header.column.id === 'name' ? (
                        <TextFilter
                          column={header.column}
                          placeholder="Enter File Name"
                        />
                      ) : header.column.id === 'updated' ? (
                        <DateFilter column={header.column} />
                      ) : null}
                    </TableHead>
                  ))}
                </TableRow>
              </React.Fragment>
            ))}
          </TableHeader>

          <TableBody>
            {table.getRowModel().rows.map((row, index) => (
              <TableRow key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {cell.column.id === 'serial'
                      ? index + 1
                      : flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <TablePagination table={table} />
    </div>
  );
};

export default FileList;
